// Prefer named Intercom — Vite/esbuild often breaks CJS default interop
// (`default is not a function`) for @intercom/messenger-js-sdk.
import { Intercom as initIntercomMessenger, boot, shutdown, update } from "@intercom/messenger-js-sdk";
import { IntercomEnvironmentConfig, IntercomIdentifyAttrs, IntercomIdentifyUser } from "./intercom-config";

const INTERCOM_SCRIPT_ID = "_intercom_npm_loader";

let widgetLoaded = false;
let booted = false;
let sessionEpoch = 0;

export function isIntercomBooted(): boolean {
  return booted;
}

/** Snapshot the current session epoch before an async Intercom boot (e.g. JWT fetch). */
export function beginIntercomBoot(): number {
  return sessionEpoch;
}

/** Returns false after logout/shutdown invalidates in-flight boot requests. */
export function isIntercomSessionCurrent(epoch: number): boolean {
  return epoch === sessionEpoch;
}

export function resetIntercomBootState(): void {
  widgetLoaded = false;
  booted = false;
  sessionEpoch = 0;
}

function messengerSettings(
  config: IntercomEnvironmentConfig,
  options: { jwt: string; user: IntercomIdentifyUser; attrs: IntercomIdentifyAttrs }
) {
  return {
    app_id: config.appId,
    api_base: config.apiBase,
    region: "us" as const,
    intercom_user_jwt: options.jwt,
    user_id: options.user.id,
    email: options.user.email,
    name: options.user.name,
    created_at: options.user.createdAtUnix,
    // Custom attributes must be nested — flat top-level unknown fields are silently dropped by Intercom.
    custom_attributes: {
      app: options.attrs.app,
      role: options.attrs.role,
      flow: options.attrs.flow,
      ...(options.attrs.businessId ? { business_id: options.attrs.businessId } : {}),
    },
  };
}

/** SDK insertBefore can no-op if document.scripts[0] is missing; append explicitly. */
function ensureIntercomWidgetScript(appId: string): void {
  if (typeof document === "undefined") {
    return;
  }
  if (document.getElementById(INTERCOM_SCRIPT_ID)) {
    return;
  }
  const script = document.createElement("script");
  script.id = INTERCOM_SCRIPT_ID;
  script.async = true;
  script.type = "text/javascript";
  script.src = `https://widget.intercom.io/widget/${appId}`;
  (document.head ?? document.body).appendChild(script);
}

function initMessenger(settings: ReturnType<typeof messengerSettings>): void {
  if (typeof initIntercomMessenger !== "function") {
    throw new Error("Intercom SDK Intercom export is not a function");
  }
  initIntercomMessenger(settings);
}

export function identifyIntercom(
  config: IntercomEnvironmentConfig | undefined,
  options: { jwt?: string; user: IntercomIdentifyUser; attrs: IntercomIdentifyAttrs }
): void {
  if (!config?.appId || !options.jwt) {
    return;
  }

  const settings = messengerSettings(config, { jwt: options.jwt, user: options.user, attrs: options.attrs });

  // initIntercomMessenger only installs the widget script once. After shutdown it
  // calls update(), which does not restart a session — use boot() instead.
  if (!widgetLoaded) {
    initMessenger(settings);
    ensureIntercomWidgetScript(config.appId);
    // Queue an authenticated boot so the session starts once the widget script loads.
    boot(settings);
    widgetLoaded = true;
    booted = true;
    return;
  }

  if (booted) {
    update(settings);
    return;
  }

  boot(settings);
  booted = true;
}

export function updateIntercom(): void {
  if (!booted) {
    return;
  }
  update({});
}

export function shutdownIntercom(): void {
  sessionEpoch++;
  if (!booted) {
    return;
  }
  shutdown();
  booted = false;
}
